<script setup lang="ts">
import {
  ref,
  onMounted,
  onBeforeUnmount,
  computed,
  nextTick,
  watch,
} from 'vue';
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
  getHighestZindex,
} from '@inscribe/signal';
import {
  OnboardingService,
  OnboardingFlowEventType,
} from '@/shared/services/onboarding/onboarding.service';
import { useOnboardingStore } from '@/V2/stores/pinia/onboarding.store';
import { logError } from '@/utils/error';
/** Emits */
const emit = defineEmits<(e: 'close') => void>();

/** Refs */
const MARGIN = 24;
const isOpen = ref<boolean>(false);
const isContentVisible = ref<boolean>(false);
const targetElement = ref<HTMLElement | null>(null);
let targetElementPosition = ref<DOMRect | null>(null);
const targetElementPositionOnResizeTimeout = ref<NodeJS.Timeout | null>(null);
const tooltipContainer = ref<HTMLElement | null>(null);
const store = useOnboardingStore();
const onboardingFlow = computed(() => store.getActiveOnboardingFlow);
const onboardingFlowStep = computed(() => store.getOnboardingFlowActiveStep);
const zIndex = getHighestZindex();
const resizeObserver = ref<ResizeObserver | null>(null);

/** Watches */
watch(onboardingFlowStep, () => {
  if (!onboardingFlowStep.value) {
    closeOnboardingTooltip();
    return;
  }
  setTargetElement();
});

/** Computed */
const maskStyle = computed(() => {
  if (!targetElementPosition.value) {
    return {
      top: 0,
      left: 0,
      width: 0,
      height: 0,
    };
  }
  const targetWidth: number = targetElementPosition.value?.width || 0;
  const targetHeight: number = targetElementPosition.value?.height || 0;
  const targetX: number = targetElementPosition.value?.x || 0;
  const targetY: number = targetElementPosition.value?.y || 0;
  const PADDING = 10;
  return {
    top: `${targetY - PADDING}px`,
    left: `${targetX - PADDING}px`,
    width: `${targetWidth + PADDING * 2}px`,
    height: `${targetHeight + PADDING * 2}px`,
  };
});

/**
 * this needs to position the tooltip in relation to the target element
 * If there is space below, position it below the target element otherwise position it above the target element
 */
const tooltipPosition = computed(() => {
  let newTop = 0;
  let newLeft = 0;
  const tooltipContainerPosition =
    tooltipContainer.value?.getBoundingClientRect();
  const targetElementMiddle =
    targetElementPosition.value?.left +
      targetElementPosition.value?.width / 2 || 0;

  if (!targetElementPosition || !tooltipContainerPosition) {
    return {
      top: `${newTop}px`,
      left: `${newLeft}px`,
    };
  }

  // best case, there is space below.
  newLeft = targetElementMiddle - tooltipContainerPosition.width / 2;
  newTop = targetElementPosition.value?.bottom + MARGIN || 0;

  // check if right corner is out of the screen
  if (newLeft + tooltipContainerPosition.width > window.innerWidth) {
    newLeft = window.innerWidth - tooltipContainerPosition.width - MARGIN;
  }

  // check if left corner is out of the screen
  if (newLeft < 0) {
    newLeft = MARGIN;
  }

  // check if top bottom is out of the screen
  if (newTop + tooltipContainerPosition.height > window.innerHeight) {
    newTop =
      targetElementPosition.value?.top -
        tooltipContainerPosition.height -
        MARGIN || 0;
  }

  return {
    top: `${sanitizeCoordinate(newTop, tooltipContainerPosition?.height || 0)}px`,
    left: `${sanitizeCoordinate(newLeft, tooltipContainerPosition?.width || 0)}px`,
  };
});

const isLastStep = computed(() => {
  return onboardingFlowStep.value?.index === onboardingFlow.value?.steps.length;
});

/** Helpers */
const setTargetElement = () => {
  if (!onboardingFlowStep.value) {
    return;
  }
  // check the targets and use the first one found
  let foundTargetElement: HTMLElement | null = null;
  for (const target of onboardingFlowStep.value?.targets || []) {
    foundTargetElement = document.querySelector(
      target as string,
    ) as HTMLElement;
    if (foundTargetElement) {
      foundTargetElement.scrollIntoView({
        behavior: 'instant',
        block: 'center',
      });
      targetElement.value = foundTargetElement;
      targetElementPosition.value = foundTargetElement.getBoundingClientRect();
      break;
    }
  }
  if (!foundTargetElement) {
    const err = new Error(
      `Onboarding step ${onboardingFlowStep.value?.index} of onboarding flow ${onboardingFlow.value?.key} target element not found ${onboardingFlowStep.value?.targets.join(', ')}`,
    );
    logError(err);
    OnboardingService.logOnboardingFlowEvent({
      eventType: OnboardingFlowEventType.TARGET_NOT_FOUND,
      targetNotFoundTargets: onboardingFlowStep.value?.targets.join(', '),
    });
    abortTourDueToError();
    throw err;
  }
};

function observeResize() {
  resizeObserver.value = new ResizeObserver(() => {
    if (targetElementPositionOnResizeTimeout.value) {
      clearTimeout(targetElementPositionOnResizeTimeout.value);
    }
    targetElementPositionOnResizeTimeout.value = setTimeout(
      setTargetElement,
      250,
    );
  });
  resizeObserver.value?.observe(document.body);
}

const sanitizeCoordinate = (coordinate: number, containerWidth: number) => {
  if (coordinate < 0) {
    return 0;
  }
  if (coordinate < window.innerWidth) {
    return coordinate;
  }
  return window.innerWidth - containerWidth - MARGIN;
};

/** Handlers */
function openOnboardingTooltip() {
  isOpen.value = true;
  setTimeout(() => {
    isContentVisible.value = true;
  }, 100);
}

function closeOnboardingTooltip() {
  isOpen.value = false;
  setTimeout(() => {
    emit('close');
  });
}
function abortTourDueToError() {
  closeOnboardingTooltip();
  OnboardingService.skipOnboardingFlow(
    onboardingFlow.value as OnboardingFlow,
    false,
  );
}
function skipTour() {
  closeOnboardingTooltip();
  OnboardingService.skipOnboardingFlow(onboardingFlow.value as OnboardingFlow);
}

function showNextStep() {
  OnboardingService.showNextStep();
}

function showPreviousStep() {
  if (onboardingFlowStep.value?.index === 1) {
    closeOnboardingTooltip();
    OnboardingService.openLaunchScreen();
  } else {
    OnboardingService.showPreviousStep();
  }
}

// Lifecycle
onMounted(() => {
  nextTick(() => {
    // needed so initially the highlight starts from 0,0 and has an animation
    setTargetElement();
  });

  observeResize();
});

onBeforeUnmount(() => {
  resizeObserver?.value?.disconnect();
});

/** Expose */
defineExpose({
  openOnboardingTooltip,
  skipTour,
  showNextStep,
  showPreviousStep,
});
</script>

<template>
  <div
    v-if="isOpen && onboardingFlow"
    class="onboarding-tooltip"
    :style="{ zIndex: zIndex }"
  >
    <!-- this is the mask that englights the target element-->
    <div class="mask i-radius-3xl" :style="maskStyle"></div>

    <!-- tooltip content -->
    <div
      ref="tooltipContainer"
      :class="{ visible: isContentVisible }"
      :style="tooltipPosition"
      class="content d-flex flex-column align-items-start justify-content-between i-p-10"
      @click.stop
    >
      <!-- close button-->
      <i-button
        :button-style="iButtonStyles.GHOST"
        :size="iButtonSizes.XSMALL"
        :type="iButtonTypes.NEUTRAL"
        left-icon="medium-delete-3"
        class="close-button"
        @button-click="skipTour"
      />

      <!-- text content -->
      <div class="title i-text-heading-6 i-mb-6">
        {{ onboardingFlowStep?.title }}
      </div>
      <div class="description i-text-body-2">
        {{ onboardingFlowStep?.description }}
      </div>

      <!-- stepper -->
      <div
        class="stepper d-flex flex-row align-items-center justify-content-center i-gap-2 i-py-9"
      >
        <div
          class="stepper-item"
          :class="{ active: onboardingFlowStep?.index >= step.index }"
          v-for="step in onboardingFlow.steps"
          :key="step.index"
          :style="{
            width: `${100 / onboardingFlow.steps.length}%`,
          }"
        ></div>
      </div>

      <!-- footer -->
      <div
        class="footer d-flex flex-row justify-content-space-between align-items-center"
      >
        <div class="left-side">
          <i-button
            :button-style="iButtonStyles.SOLID_LIGHT"
            :size="iButtonSizes.SMALL"
            :type="iButtonTypes.NEUTRAL"
            label="Previous"
            @button-click="showPreviousStep"
          />
        </div>
        <div class="right-side d-flex flex-row align-items-center i-gap-3">
          <i-button
            :button-style="iButtonStyles.SOLID_LIGHT"
            :size="iButtonSizes.SMALL"
            :type="iButtonTypes.NEUTRAL"
            label="Skip Tour"
            @button-click="skipTour"
          />
          <i-button
            :button-style="iButtonStyles.SOLID_DARK"
            :size="iButtonSizes.SMALL"
            :type="iButtonTypes.PRIMARY"
            :label="isLastStep ? 'Get Started' : 'Continue'"
            :left-icon="isLastStep ? 'small_check_1' : undefined"
            :right-icon="isLastStep ? undefined : 'medium-chevron-right'"
            @button-click="showNextStep"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.onboarding-tooltip {
  $border-radius: 24px;

  inset: 0;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  .mask {
    position: absolute;
    box-shadow: rgba(0, 0, 0, 0.5) 0px 0px 0px 9999px;
    transition: all 0.3s ease-in-out;
  }

  .close-button {
    position: absolute;
    top: 20px;
    right: 20px;
  }

  .content {
    position: absolute;
    top: 0;
    left: 0;
    width: 512px;
    background-color: var(--base);
    border-radius: $border-radius;
    transition: all 0.3s ease-in-out;
    opacity: 0;
    visibility: hidden;

    &.visible {
      opacity: 1;
      visibility: visible;
    }

    .title,
    .description {
      color: var(--text-neutral-bold);
    }

    .stepper {
      width: 100%;

      .stepper-item {
        height: 6px;
        position: relative;
        border-radius: 24cqi;
        background-color: var(--border-neutral-subtle);

        &:after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 0%;
          height: 100%;
          background-color: var(--fill-primary);
          transition: all 0.5s ease-in-out;
          border-radius: 24px;
        }

        &.active {
          &:after {
            width: 100%;
          }
        }
      }
    }
  }

  .footer {
    width: 100%;
  }
}
</style>
